import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useColadas } from '@/hooks/useColadas';
import { nombrePlantaDisplay } from '@/lib/plantas';
import { cn } from '@/lib/utils';
import type { ColadaListadoItem } from '@adelante/shared';
import { Check, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

/**
 * Picker de coladas — pensado para que el laboratorista elija fácil una
 * colada existente al crear una muestra.
 *
 * Por defecto muestra los últimos 30 días, todas las plantas, ordenadas
 * de más reciente a más antigua. Tiene búsqueda libre por destino/receta
 * (mismo `q` del endpoint de coladas).
 *
 * Diseño: lista inline (no popover) para que se vea cuántas opciones hay,
 * misma pauta visual que PickerObras.
 */

interface Props {
  valor: number | null;
  onChange: (idColada: number | null, colada: ColadaListadoItem | null) => void;
  maxAltoPx?: number;
}

export function PickerColadas({ valor, onChange, maxAltoPx = 280 }: Props) {
  const [q, setQ] = useState('');

  // Default: últimos 30 días. Usuario puede escribir buscar para ampliar
  // por destino/cliente.
  const desde = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  }, []);

  const filtros = useMemo(
    () => ({
      desde,
      q: q.trim() || undefined,
      // Excluimos anuladas — no tiene sentido vincular muestras a coladas anuladas.
      estado: ['sugerida', 'confirmada', 'digitada', 'cerrada'] as Array<
        'sugerida' | 'confirmada' | 'digitada' | 'cerrada'
      >,
      por_pagina: 100,
    }),
    [desde, q],
  );

  const consulta = useColadas(filtros);
  const coladas = consulta.data?.data?.coladas ?? [];

  return (
    <div className="grid gap-2">
      <Label htmlFor="picker-coladas-q">Buscar colada (últimos 30 días)</Label>
      <div className="relative">
        <Search
          className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          id="picker-coladas-q"
          placeholder="Destino, receta, cliente..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-8"
        />
      </div>

      <div className="text-xs text-muted-foreground">
        {consulta.isLoading ? 'Cargando…' : `${coladas.length} colada(s)`}
      </div>

      <div className="overflow-y-auto rounded-md border" style={{ maxHeight: `${maxAltoPx}px` }}>
        {consulta.error && (
          <p className="p-3 text-sm text-destructive">
            Error al cargar coladas: {consulta.error.message}
          </p>
        )}
        {!consulta.isLoading && !consulta.error && coladas.length === 0 && (
          <p className="p-3 text-center text-sm text-muted-foreground">
            No hay coladas que coincidan. Probá ampliar la búsqueda o crear la muestra sin colada
            (planta Fiori u otra externa).
          </p>
        )}
        {coladas.map((c) => {
          const seleccionada = c.id_colada === valor;
          return (
            <button
              key={c.id_colada}
              type="button"
              onClick={() => onChange(c.id_colada, c)}
              className={cn(
                'flex w-full items-center justify-between gap-3 border-b px-3 py-2 text-left text-sm last:border-b-0 hover:bg-zinc-50',
                seleccionada && 'bg-emerald-50 hover:bg-emerald-50',
              )}
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="font-mono font-medium">#{c.codigo_interno}</span>
                  <span className="text-muted-foreground">·</span>
                  <span>{nombrePlantaDisplay(c.planta_nombre)}</span>
                  <span className="text-muted-foreground">·</span>
                  <span className="font-mono text-muted-foreground">
                    {c.fecha_inicio.slice(0, 10)}
                  </span>
                  <span className="text-muted-foreground">·</span>
                  <span className="font-mono tabular-nums">{c.m3_producidos.toFixed(2)} m³</span>
                </div>
                <div className="mt-0.5 truncate text-sm">
                  <span className="font-medium">{c.receta_blend_nombre}</span>
                  {c.descripcion_receta_bc && (
                    <span className="ml-2 text-muted-foreground">→ {c.descripcion_receta_bc}</span>
                  )}
                </div>
                <div className="truncate text-xs text-muted-foreground">{c.destino_display}</div>
              </div>
              {seleccionada && <Check className="h-4 w-4 flex-none text-emerald-600" />}
            </button>
          );
        })}
      </div>

      {valor !== null && (
        <button
          type="button"
          onClick={() => onChange(null, null)}
          className="justify-self-start text-xs text-muted-foreground hover:text-foreground underline"
        >
          Quitar selección de colada
        </button>
      )}
    </div>
  );
}
